import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import { escalationLogService, type EscalationLogFilter } from '@/services/escalation-log.service';

const QUERY_ROOT = 'escalation-logs';

const pagesQueryAtom = (filter: EscalationLogFilter) =>
	atomWithInfiniteQuery((get) => ({
		// The brand is in the key: two brands never read each other's log.
		queryKey: [QUERY_ROOT, buScopeKey(get), filter],
		queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
			escalationLogService.fetchPage(filter, pageParam, buScopeParams(get)),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage: { entries: { id: string }[]; hasMore: boolean }) =>
			lastPage.hasMore ? lastPage.entries.at(-1)?.id : undefined,
		enabled: get(isAuthenticatedAtom)
	}));

/** The selected brand's escalations, newest first, a page at a time. `filter` must be memoised. */
export function useEscalationLog(filter: EscalationLogFilter) {
	const queryAtom = useMemo(() => pagesQueryAtom(filter), [filter]);
	const { data, isLoading, isError, isFetchNextPageError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useAtomValue(queryAtom);
	const entries = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	return {
		entries,
		isLoading,
		isError,
		loadMoreFailed: isFetchNextPageError,
		refetch,
		hasMore: Boolean(hasNextPage),
		loadingMore: isFetchingNextPage,
		loadMore
	};
}

const summaryQueryAtom = (range: Pick<EscalationLogFilter, 'from' | 'to'>) =>
	atomWithQuery((get) => ({
		queryKey: [QUERY_ROOT, 'summary', buScopeKey(get), range],
		queryFn: () => escalationLogService.fetchSummary(range, buScopeParams(get)),
		enabled: get(isAuthenticatedAtom)
	}));

/** How the selected brand's escalations ended over the dates. `range` must be memoised. */
export function useEscalationSummary(range: Pick<EscalationLogFilter, 'from' | 'to'>) {
	const queryAtom = useMemo(() => summaryQueryAtom(range), [range]);
	return useAtomValue(queryAtom).data;
}
